import {HttpStatusCode} from '@angular/common/http';
import {Component, OnDestroy} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    EntityType, FraudReportApiService, FraudReportResponse,
    ReferenceBookResponse,
    ReferenceBookType,
} from '@npck/af-frontend-api';
import {TableEvent} from '../../../../components/full-table/full-table.component';
import {Path} from '../../../../path';
import {downloadFromBlob, LocalizationService, NESTED_TAB, RouterService, rxSpinner, TAB} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import {Observable, of, Subscription, zip} from 'rxjs';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {DictionaryHolderService, findValueInDictionary} from '../../../../domain/dictionary-holder.service';
import {FRAUD_REPORT_ID, FRAUD_REPORT_TYPE} from '../../../../domain/query-params.keys';
import {ANALYTIC_FRAUD_REPORTS_TAB, AnalyticsTab} from '../../analytics.component';
import {FraudReportSignDialogComponent} from './report-sign-dialog/fraud-report-sign-dialog.component';

@Component({
    templateUrl: 'fraud-report-details.component.html',
    styleUrls: ['fraud-report-details.component.scss'],
    standalone: false,
})
export class FraudReportDetailsComponent implements OnDestroy {
    EntityType = EntityType;
    tabPathToTab: Record<string, AnalyticsTab> = {'fraud-reports': ANALYTIC_FRAUD_REPORTS_TAB};
    fraudReport: FraudReportResponse;
    fraudReportId: string;
    tabs: FraudReportDetailsTab[] = [
        'tab-SELF_DETECTED_ATTEMPTED_TRANSACTIONS',
        'tab-CLIENT_CONFIRMED_COMPLETED_TRANSACTIONS',
        'tab-CLIENT_NOT_CONFIRMED_COMPLETED_TRANSACTIONS',
        'tab-REFUNDS',
        'tab-AC_ATTEMPTED_TRANSACTIONS',
        'tab-AC_BLOCKED_ACCOUNT_TRANSACTIONS',
        'tabFraudReportFiles',
        'tabHistory',
    ];
    selectedTabIndex: number;
    activeTab: FraudReportDetailsTab;
    exportReportSubscription: Subscription;
    organizationName: string;
    transactionTypes: ReferenceBookResponse[];
    transactionCategories: ReferenceBookResponse[];
    transactionStatuses: ReferenceBookResponse[];
    currencies: ReferenceBookResponse[];
    refundTypes: ReferenceBookResponse[];
    refundStatuses: ReferenceBookResponse[];

    constructor(
        public authHolderService: AuthHolderService,
        private activatedRoute: ActivatedRoute,
        private routerService: RouterService,
        private fraudReportApiService: FraudReportApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private dialogService: DialogService,
        private snackbarService: SnackbarService,
        private localizationService: LocalizationService,
    ) {
        this.fraudReportId = this.activatedRoute.snapshot.paramMap.get(FRAUD_REPORT_ID);
        this.activeTab = this.activatedRoute.snapshot.queryParamMap.get(NESTED_TAB) as FraudReportDetailsTab
            || 'tab-SELF_DETECTED_ATTEMPTED_TRANSACTIONS';
        zip([
            this.fraudReportApiService.getFraudReport(this.fraudReportId),
            this.dictionaryHolderService.getReference(ReferenceBookType.TRANSACTION_TYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.TRANSACTION_CATEGORY),
            this.dictionaryHolderService.getReference(ReferenceBookType.TRANSACTION_STATUS),
            this.dictionaryHolderService.getReference(ReferenceBookType.CURRENCY),
            this.dictionaryHolderService.getReference(ReferenceBookType.BIN),
            this.dictionaryHolderService.getReference(ReferenceBookType.REFUND_TYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.REFUND_STATUS),
        ])
            .pipe(rxSpinner('initial'))
            .subscribe({
                next: ([
                    response,
                    transactionTypes,
                    transactionCategories,
                    transactionStatuses,
                    currencies,
                    bins,
                    refundTypes,
                    refundStatuses]) => {

                    this.transactionTypes = transactionTypes;
                    this.transactionCategories = transactionCategories;
                    this.transactionStatuses = transactionStatuses;
                    this.currencies = currencies;
                    this.refundTypes = refundTypes;
                    this.refundStatuses = refundStatuses;
                    this.organizationName = findValueInDictionary(bins, response.bin, 'name_ru') ?? '-';
                    this.setFraudReport(response);
                },
                error: error => {
                    if (error.status === HttpStatusCode.Forbidden) {
                        this.navigateBack();
                        this.snackbarService.showSnackBar(
                            'i18n_af_snackBarMessageFraudReportAccessRestricted',
                            'error',
                            false,
                            {fraudReportId: this.fraudReportId});
                        return;
                    }
                    throw error;
                },
            });
    }

    get reportSection(): string {
        return this.activeTab.split('-').pop();
    }

    get hasReportAccess(): boolean {
        if (this.authHolderService.hasAccess(['npck-admin'])) {
            return true;
        }

        return this.authHolderService.hasAccess(['organization-supervisor'])
            && this.authHolderService.organizationBin == this.fraudReport?.bin;
    }

    ngOnDestroy(): void {
        this.exportReportSubscription?.unsubscribe();
    }

    handleNavigation($event: number): void {
        this.activeTab = this.tabs[$event];
        this.routerService.navigate(Path.ANALYTICS_FRAUD_REPORT_DETAILS, {
            pathParams: {
                [FRAUD_REPORT_TYPE]: this.fraudReport.reportType,
                [FRAUD_REPORT_ID]: this.fraudReportId,
            },
            queryParams: {
                [TAB]: ANALYTIC_FRAUD_REPORTS_TAB,
                [NESTED_TAB]: this.activeTab,
            },
            queryParamsHandling: 'merge',
        });
    }

    navigateBack = (): Observable<boolean> => {
        this.routerService.navigate(Path.ANALYTICS_FRAUD_REPORTS, {
            pathParams: {
                [FRAUD_REPORT_TYPE]: this.fraudReport.reportType,
            },
            queryParams: {
                [TAB]: ANALYTIC_FRAUD_REPORTS_TAB
            },
        });
        return of(false);
    };

    downloadReport(): void {
        const request = this.fraudReportApiService.downloadFraudReportExcel(this.fraudReportId);

        const fileName = this.localizationService.getLocalizedKey('i18n_af_fraudReports#' + this.fraudReport.reportType)
            + ' (' + this.organizationName + ')';
        this.exportReportSubscription = request.subscribe({
            next: file => {
                downloadFromBlob(file, `${fileName}.xlsx`);
                this.snackbarService.showSnackBar('i18n_af_snackBarMessageFraudReportSuccessfullyExported');
            }, error: err => {
                this.snackbarService.showSnackBar(
                    'i18n_af_snackBarMessageFailedToDownloadFraudReport#EXCEL',
                    'error',
                    false,
                );
                console.error(err);
            },
        });
    };

    openRegenerateReportDialog(): void {
        this.dialogService.openDialog({
            title: 'i18n_af_dialogFraudReportRegenerateConfirmationDialogTitle',
            buttonOkText: 'i18n_af_buttonRegenerate',
            buttonCancelText: 'i18n_af_buttonCancel',
        })
            .subscribe(result => {
                if (result) {
                    this.regenerateReport();
                }
            });
    }

    openSignDialog(): void {
        this.dialogService.openCustomDialog<FraudReportSignDialogComponent, boolean>(
            FraudReportSignDialogComponent,
            null,
            {autoFocus: false, disableClose: true},
        ).subscribe(result => {
            if (!!result) {
                this.signReport();
            }
        });
    }

    private regenerateReport(): void {
        this.fraudReportApiService.regenerateFraudReport(this.fraudReport.id)
            .subscribe({
                next: (response) => {
                    this.setFraudReport(response);
                    this.snackbarService.showSnackBar('i18n_af_snackBarMessageFraudReportRegenerateSuccess');
                },
                error: err => {
                    this.snackbarService.showSnackBar(
                        'i18n_af_snackBarMessageFraudReportRegenerateFailed',
                        'error',
                        false,
                    );
                    console.error(err);
                },
            });
    }

    private signReport(): void {
        this.fraudReportApiService.getFraudReportSignUrl(this.fraudReport.id, window.location.host)
            .subscribe({
                next: (response) => {
                    if (response.url) {
                        window.location.href = response.url;
                    }
                },
                error: err => {
                    this.snackbarService.showSnackBar(
                        'i18n_af_snackBarMessageFraudReportSignFailed',
                        'error',
                        false,
                    );
                    console.error(err);
                },
            });
    }

    private setFraudReport(response: FraudReportResponse): void {
        this.fraudReport = response;
        this.selectedTabIndex = Math.max(0, this.tabs.findIndex(tab => tab === this.activeTab));
    }
}

export declare type FraudReportDetailsTab =
    'tab-SELF_DETECTED_ATTEMPTED_TRANSACTIONS'
    | 'tab-CLIENT_CONFIRMED_COMPLETED_TRANSACTIONS'
    | 'tab-CLIENT_NOT_CONFIRMED_COMPLETED_TRANSACTIONS'
    | 'tab-REFUNDS'
    | 'tab-AC_ATTEMPTED_TRANSACTIONS'
    | 'tab-AC_BLOCKED_ACCOUNT_TRANSACTIONS'
    | 'tabFraudReportFiles'
    | 'tabHistory';

export interface FraudReportDetailsTabState {
    tableEvent: TableEvent;
}
